import { LinkProps } from '@mui/material/Link';
import { LinkBehavior } from 'components';

export const palette = {
  // Brand
  primaryOrange: '#E66A10',
  lightOrange: '#F39C12',
  brandDark: '#32363F',

  // Text
  textDark: '#32363F',
  textGray: '#6B7280',
  textLight: '#9CA3AF',
  blackText: '#0F172A',
  whiteText: '#FFF',
  subTittle: '#6B7280',

  // Backgrounds
  bgWhite: '#FFFFFF',
  bgLight: '#F9FAFB',
  backgroundTheme: '#F9FAFB',
  backgroundThemeLight: '#FFFFFF',
  textOnDark: '#F3F4F6',

  // Borders
  borderColor: '#E5E7EB',

  // Semantic — success
  success: '#10B981',
  successLight: '#D1FAE5',
  success500: '#10B981',
  success600: '#059669',
  success700: '#047857',
  success800: '#065F46',

  // Semantic — danger / error
  danger: '#EF4444',
  dangerLight: '#FEE2E2',
  error500: '#EF4444',
  error600: '#DC2626',
  error800: '#991B1B',
  danger400: '#F87171',
  danger600: '#DC2626',
  danger700: '#B91C1C',
  danger800: '#991B1B',
  danger900: '#7F1D1D',
  notificationBadge: '#DC2626',

  // Semantic — warning
  warningDark: '#D97706',
  warningLight: '#FEF3C7',
  warning50: '#FFFBEB',
  warning600: '#D97706',
  warning700: '#B45309',
  warning800: '#92400E',
  amber500: '#F59E0B',

  // Semantic — info
  info: '#3B82F6',
  infoLight: '#EFF6FF',
  info50: '#EFF6FF',
  info500: '#3B82F6',
  info600: '#2563EB',
  info700: '#1D4ED8',
  info900: '#1E3A8A',

  // Grey scale
  gray: '#6B7280',
  grey50: '#F9FAFB',
  grey100: '#F3F4F6',
  grey200: '#E5E7EB',
  grey300: '#D1D5DB',
  grey400: '#9CA3AF',
  grey500: '#6B7280',
  grey600: '#4B5563',
  grey700: '#374151',
  grey800: '#1F2937',
  grey900: '#111827',

  // Misc legacy (kept for backward compatibility)
  light100: '#E7E5E4',
  main: '#31887E',
  searchResult: '#ffff00',
  absoluteDark: '#000000',
  white: '#FFFFFF',
  rowHighlight: '#EFF6FF',
  rowCancellationSubmittedHighlight: '#FEE2E2',
  rowUnreadHighlight: '#F5F3FF',

  // Legacy primary teal (kept so existing non-login components don't break)
  primary50: '#FEF7EE',
  primary100: '#FDECD8',
  primary200: '#FAD4AB',
  primary300: '#F7B877',
  primary400: '#F1933C',
  primary500: '#E66A10',
  primary600: '#D4580E',
  primary700: '#C24A0C',
  primary800: '#A83D0A',
  primary900: '#8C3208',

  // Legacy secondary (kept for backward compat)
  secondary50: '#E6EEF0',
  secondary100: '#C1D5DA',
  secondary500: '#307282',
  secondary600: '#2B6A7A',
  secondary700: '#245F6F',
  secondary800: '#1E5565',
  secondary900: '#134252',

  // Misc
  muted50: '#F0F0EF',
  muted100: '#DAD8D7',
  muted200: '#C1BFBD',
  muted300: '#A8A5A2',
  muted400: '#96918E',
  muted500: '#837E7A',
  muted600: '#7B7672',
  muted700: '#706B67',
  muted800: '#66615D',
  muted900: '#534E4A',

  teal600: '#0D9488',
  darkText: '#32363F',
  purple800: '#693082',
  violet700: '#6D28D9',
  purple50: '#FAF5FF',
  purple700: '#7E22CE',
  bluegray400: '#94A3B8',
  bluegray600: '#475569',
  truegray300: '#D4D4D4',
  truegray400: '#A3A3A3',
  truegray600: '#525252',
  truegray700: '#404040',
  truegray800: '#262626',
  truegray900: '#171717',
};

export const dimensions = {
  appBarHeight: 6,
  notificationPadding: 1,
  appDrawerWidth: 17.3,
  viewHorizontalPadding: 3.5,
  viewVerticalPadding: 2.5,
};

export const zIndex = {
  mainNavigation: 7,
  orderLeftPane: 4,
  orderChat: 3,
  batchingOrderNumberPopup: 2,
};

export const time = {
  tooltipEnterDelay: 1000,
  tooltipEnterNextDelay: 1000,
};

export const paddings = {
  componentPaddingSmall: '1.6rem',
};

export const popperTimeSx = {
  '& .MuiClock-amButton, & .MuiClock-pmButton': {
    width: '4.2rem',
  },
  '& .MuiClockNumber-root': {
    fontSize: '1.6rem',
  },
};

const FONT = ['Montserrat', 'Inter', 'sans-serif'].join(',');
const TRANSITION = 'all 0.3s ease';

export const themeConfig = {
  custom: {
    palette,
    dimensions,
    zIndex,
    time,
    paddings,
  },

  palette: {
    disabled: {
      main: palette.muted600,
      contrastText: '#fff',
    },
    primary: {
      main: palette.primaryOrange,
      light: palette.lightOrange,
      dark: '#C24A0C',
      contrastText: '#fff',
    },
    secondary: {
      main: palette.textGray,
      contrastText: '#fff',
    },
    success: {
      main: palette.success,
      light: palette.successLight,
      contrastText: '#fff',
    },
    error: {
      main: palette.danger,
      light: palette.dangerLight,
      contrastText: '#fff',
    },
    warning: {
      main: palette.warningDark,
      light: palette.warningLight,
      contrastText: '#fff',
    },
    info: {
      main: palette.info,
      light: palette.infoLight,
      contrastText: '#fff',
    },
    background: {
      default: palette.bgLight,
      paper: palette.bgWhite,
    },
    text: {
      primary: palette.textDark,
      secondary: palette.textGray,
      disabled: palette.textLight,
    },
    divider: palette.borderColor,
  },

  typography: {
    fontFamily: FONT,
    fontSize: 14,
    h1: {
      fontFamily: FONT,
      fontSize: '2rem',
      fontWeight: 700,
      color: palette.textDark,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontFamily: FONT,
      fontSize: '1.25rem',
      fontWeight: 700,
      color: palette.textDark,
      lineHeight: 1.25,
      letterSpacing: '-0.3px',
    },
    h3: {
      fontFamily: FONT,
      fontSize: '1rem',
      fontWeight: 600,
      color: palette.textDark,
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: FONT,
      fontSize: '0.875rem',
      fontWeight: 600,
      color: palette.textDark,
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: FONT,
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: palette.textDark,
      lineHeight: 1.25,
    },
    h6: {
      fontFamily: FONT,
      fontSize: '0.75rem',
      fontWeight: 600,
      color: palette.textDark,
    },
    subtitle1: {
      fontFamily: FONT,
      fontWeight: 400,
      fontSize: '0.9375rem',
      color: palette.textGray,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontFamily: FONT,
      fontSize: '0.6875rem',
      fontWeight: 700,
      color: palette.textGray,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: FONT,
      fontSize: '0.875rem',
      color: palette.textDark,
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: FONT,
      fontSize: '0.8125rem',
      color: palette.textGray,
      lineHeight: 1.5,
    },
    caption: {
      fontFamily: FONT,
      fontSize: '0.6875rem',
      fontWeight: 700,
      color: palette.textGray,
      textTransform: 'uppercase' as const,
      letterSpacing: '1px',
    },
    overline: {
      fontFamily: FONT,
      fontSize: '0.6875rem',
      fontWeight: 700,
      color: palette.textGray,
      textTransform: 'uppercase' as const,
      letterSpacing: '1px',
    },
    button: {
      fontFamily: FONT,
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.5px',
      textTransform: 'uppercase' as const,
    },
  },

  shape: {
    borderRadius: 8,
  },

  components: {
    // --- Buttons ---
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.5px',
          transition: TRANSITION,
          textTransform: 'uppercase' as const,
          padding: '10px 20px',
        },
        contained: {
          background: `linear-gradient(135deg, ${palette.lightOrange}, ${palette.primaryOrange})`,
          boxShadow: '0 4px 15px rgba(230, 106, 16, 0.2)',
          '&:hover': {
            background: `linear-gradient(135deg, ${palette.lightOrange}, ${palette.primaryOrange})`,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(230, 106, 16, 0.3)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 4px 15px rgba(230, 106, 16, 0.2)',
          },
          '&.Mui-disabled': {
            background: palette.grey200,
            color: palette.grey400,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: palette.primaryOrange,
          color: palette.primaryOrange,
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: palette.primaryOrange,
            color: '#fff',
          },
        },
        text: {
          color: palette.primaryOrange,
          '&:hover': {
            backgroundColor: 'rgba(230, 106, 16, 0.05)',
          },
        },
        sizeSmall: {
          padding: '6px 14px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiLoadingButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.5px',
          textTransform: 'uppercase' as const,
          transition: TRANSITION,
          '&.MuiLoadingButton-loading': {
            transform: 'none',
          },
        },
        loadingIndicator: {
          color: '#fff',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: TRANSITION,
          '&:hover': {
            backgroundColor: 'rgba(230, 106, 16, 0.08)',
            color: palette.primaryOrange,
          },
        },
      },
    },

    // --- Inputs ---
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: palette.bgLight,
          fontFamily: FONT,
          fontSize: '0.875rem',
          transition: TRANSITION,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primaryOrange,
          },
          '&.Mui-focused': {
            backgroundColor: palette.bgWhite,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primaryOrange,
            borderWidth: '1px',
            boxShadow: '0 0 0 4px rgba(230, 106, 16, 0.1)',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.danger,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.1)',
          },
        },
        notchedOutline: {
          borderColor: palette.borderColor,
        },
        input: {
          padding: '12px 16px',
          fontSize: '0.875rem',
          color: palette.textDark,
          fontFamily: FONT,
          '&::placeholder': {
            color: palette.textGray,
            opacity: 1,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          color: palette.textDark,
          '& input::placeholder': {
            color: palette.textGray,
            opacity: 1,
          },
        },
        input: {
          fontSize: '0.875rem',
          padding: '12px 16px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          color: palette.textGray,
          '&.Mui-focused': {
            color: palette.primaryOrange,
          },
          '&.Mui-error': {
            color: palette.danger,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.75rem',
          marginTop: '4px',
        },
      },
    },

    // --- Paper / Cards ---
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: palette.bgWhite,
          border: `1px solid ${palette.borderColor}`,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.bgWhite,
          border: `1px solid ${palette.borderColor}`,
          borderRadius: '12px',
          boxShadow: 'none',
          transition: TRANSITION,
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.04)',
            borderColor: 'transparent',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },

    // --- Divider ---
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.borderColor,
        },
        wrapper: {
          fontSize: '0.75rem',
          fontWeight: 600,
          fontFamily: FONT,
          color: palette.textGray,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          padding: '0 16px',
        },
      },
    },

    // --- Chip ---
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.6875rem',
          fontWeight: 600,
          borderRadius: '12px',
          height: '24px',
          transition: TRANSITION,
        },
        colorDefault: {
          backgroundColor: palette.grey100,
          color: palette.textGray,
        },
        colorPrimary: {
          backgroundColor: 'rgba(230, 106, 16, 0.1)',
          color: palette.primaryOrange,
        },
        colorSuccess: {
          backgroundColor: palette.successLight,
          color: palette.success,
        },
        colorError: {
          backgroundColor: palette.dangerLight,
          color: palette.danger,
        },
        colorWarning: {
          backgroundColor: palette.warningLight,
          color: palette.warningDark,
        },
        colorInfo: {
          backgroundColor: palette.infoLight,
          color: palette.info,
        },
        label: {
          padding: '0 10px',
        },
      },
    },

    // --- List / Navigation ---
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: '0',
          paddingBottom: '0',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.6875rem',
          fontWeight: 700,
          color: palette.textGray,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          lineHeight: '2rem',
          padding: '16px 24px 8px',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: palette.textGray,
          padding: '10px 24px',
          marginBottom: '0',
          borderLeft: '3px solid transparent',
          borderRadius: '0',
          transition: TRANSITION,
          '&:hover': {
            backgroundColor: palette.bgLight,
            color: palette.textDark,
            borderLeftColor: 'transparent',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(230, 106, 16, 0.05)',
            color: palette.primaryOrange,
            fontWeight: 600,
            borderLeftColor: palette.primaryOrange,
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(230, 106, 16, 0.08)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '32px',
          color: 'inherit',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
          span: {
            fontFamily: FONT,
            fontSize: '0.875rem !important',
            fontWeight: 'inherit',
          },
        },
      },
    },

    // --- Menu ---
    MuiMenu: {
      styleOverrides: {
        root: {
          '.MuiPaper-root': {
            borderRadius: '12px',
            border: `1px solid ${palette.borderColor}`,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          },
          ul: {
            padding: '4px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: palette.textDark,
          borderRadius: '8px',
          padding: '8px 12px',
          marginBottom: '2px',
          transition: TRANSITION,
          '&:hover': {
            backgroundColor: palette.bgLight,
            color: palette.textDark,
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(230, 106, 16, 0.08)',
            color: palette.primaryOrange,
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(230, 106, 16, 0.12)',
          },
        },
      },
    },

    // --- Table ---
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          borderBottom: `1px solid ${palette.borderColor}`,
          color: palette.textDark,
          padding: '12px 16px',
        },
        head: {
          fontSize: '0.6875rem',
          fontWeight: 700,
          color: palette.textGray,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          backgroundColor: palette.bgLight,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: TRANSITION,
          '&:hover': {
            backgroundColor: palette.bgLight,
          },
        },
      },
    },

    // --- Tooltip ---
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: FONT,
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: palette.textDark,
          borderRadius: '6px',
          padding: '6px 10px',
        },
        arrow: {
          color: palette.textDark,
        },
      },
    },

    // --- Switch ---
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          '&.Mui-checked': {
            color: palette.primaryOrange,
            '& + .MuiSwitch-track': {
              backgroundColor: palette.primaryOrange,
              opacity: 0.5,
            },
          },
        },
        track: {
          borderRadius: '12px',
          backgroundColor: palette.grey300,
        },
        thumb: {
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        },
      },
    },

    // --- Alert ---
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          fontWeight: 500,
          borderRadius: '8px',
        },
        standardSuccess: {
          backgroundColor: palette.successLight,
          color: palette.success,
        },
        standardError: {
          backgroundColor: palette.dangerLight,
          color: palette.danger,
        },
        standardWarning: {
          backgroundColor: palette.warningLight,
          color: palette.warningDark,
        },
        standardInfo: {
          backgroundColor: palette.infoLight,
          color: palette.info,
        },
      },
    },

    // --- Links ---
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
      styleOverrides: {
        root: {
          fontFamily: FONT,
          color: palette.primaryOrange,
          fontWeight: 500,
          textDecoration: 'none',
          transition: TRANSITION,
        },
      },
    },

    // --- ButtonBase ---
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
        },
      },
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },

    // --- Tabs ---
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: palette.textGray,
          textTransform: 'none',
          transition: TRANSITION,
          '&.Mui-selected': {
            color: palette.primaryOrange,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: palette.primaryOrange,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    // --- Select ---
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: palette.textGray,
        },
      },
    },

    // --- Autocomplete ---
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        },
        option: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(230, 106, 16, 0.08)',
            color: palette.primaryOrange,
          },
        },
      },
    },

    // --- Dialog ---
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          border: `1px solid ${palette.borderColor}`,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '1.125rem',
          fontWeight: 700,
          color: palette.textDark,
          padding: '24px 24px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0 24px 16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: '8px',
        },
      },
    },

    // --- Breadcrumbs ---
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.875rem',
          color: palette.textGray,
        },
        separator: {
          color: palette.grey300,
        },
      },
    },

    // --- LinearProgress ---
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: '3px',
          height: '6px',
          backgroundColor: palette.grey100,
        },
        barColorPrimary: {
          backgroundColor: palette.primaryOrange,
          borderRadius: '3px',
        },
        barColorSuccess: {
          backgroundColor: palette.success,
          borderRadius: '3px',
        },
      },
    },
  },
};

type BaseStyle = {
  [key: string]: any;
};

export const tourStyles = {
  popover: (base: BaseStyle) =>
    ({
      ...base,
      margin: '1rem',
      '--reactour-accent': palette.primaryOrange,
    } as any),
  maskArea: (base: BaseStyle) => ({ ...base } as any),
  maskWrapper: (base: BaseStyle) =>
    ({ ...base, color: palette.textGray } as any),
  badge: (base: BaseStyle) =>
    ({ ...base, left: 'auto', right: '-0.8125em' } as any),
  controls: (base: BaseStyle) => ({ ...base, marginTop: 100 } as any),
  close: (base: BaseStyle) =>
    ({ ...base, right: 'auto', left: 8, top: 8 } as any),
};
